export const CHECKOUT_URL = 'https://obd-en.impultienda.ar/checkout/';

export const MOCKUP_IMAGE = 'https://cdn.magicpatterns.com/uploads/cM8WMtFaeCoomZLnQCFkYQ/MOCKUP_OBD2-EN.png';
export const AUTHOR_IMAGE =
  'https://cdn.magicpatterns.com/uploads/dFbaRNWBQo6sQWz3L9RYzw/JASON_MITCHELL_-_ECU_DECISION.png';

export const heroIncludes = [
  'OBD2 fault code interpretation guides',
  'Step-by-step diagnostic workflows',
  'Check Engine troubleshooting decision trees',
  'Sensor and actuator testing frameworks',
  'Live data interpretation guides',
  'Automotive diagnostic case studies',
  'Printable diagnostic checklists',
  'Professional fault investigation templates',
];

export const sampleCodes = ['P0300', 'P0171', 'P0420'];

export const nextQuestions = [
  'Should you inspect a sensor?',
  'Check the wiring?',
  'Review live data?',
  'Investigate a mechanical problem?',
  'Or perform additional tests before making any conclusions?',
];

export const principleSteps = [
  'Understand the code',
  'Evaluate the evidence',
  'Choose the next test',
  'Verify the result',
];

export const beforeItems = [
  'You retrieve fault codes but aren’t sure where to begin.',
  'You search multiple websites trying to understand possible causes.',
  'You suspect a sensor without confirming whether it’s actually defective.',
  'You struggle to interpret live diagnostic data.',
  'You replace components without enough supporting evidence.',
  'You repeat tests because your previous findings weren’t documented.',
  'You feel overwhelmed when several fault codes appear simultaneously.',
  'Every complicated diagnosis feels like starting from zero.',
];

export const afterItems = [
  'You follow an organized process for interpreting diagnostic trouble codes.',
  'You understand the difference between symptoms, codes, and confirmed faults.',
  'You identify which vehicle systems require further investigation.',
  'You use structured workflows to plan appropriate diagnostic checks.',
  'You organize live data observations and test results.',
  'You compare possible causes against the available evidence.',
  'You document your findings before making repair decisions.',
  'You develop a more consistent approach to future diagnostic cases.',
];

export const painPoints = [
  {
    title: 'Your scanner shows a code… but you don’t know what to check first.',
    body: 'You understand the code description, but you’re uncertain which diagnostic test would provide the most useful information. The Blueprint helps you organize the possible causes and determine a logical starting point.',
  },
  {
    title: 'You’ve replaced a sensor… but the problem didn’t disappear.',
    body: 'A code mentioned a particular sensor, so replacing it seemed reasonable. But the actual issue could involve wiring, electrical supply, operating conditions, or another system. Learn how to distinguish a code description from a confirmed component failure.',
  },
  {
    title: 'You see multiple fault codes and don’t know which one to investigate first.',
    body: 'One vehicle. Several codes. Multiple possible explanations. The Blueprint introduces frameworks for organizing related codes, symptoms, and available evidence before selecting the next check.',
  },
  {
    title: 'You have live data… but you’re not sure how to interpret it.',
    body: 'Your scanner displays sensor readings, fuel trims, temperatures, and operating information. But numbers become useful only when you understand what they represent and how to evaluate them against reliable specifications.',
  },
  {
    title: 'You spend too much time searching for diagnostic information.',
    body: 'You switch between code databases, technical articles, wiring diagrams, and repair manuals. The Blueprint helps you organize your investigation so the information you collect supports a clear diagnostic question.',
  },
  {
    title: 'You want to become more confident with modern automotive electronics.',
    body: 'Vehicles are becoming increasingly dependent on sensors, electronic control modules, and diagnostic communication systems. You want a repeatable approach to electronic troubleshooting instead of relying on guesswork.',
  },
];

export const volumes = [
  {
    numeral: 'I',
    color: '#3B82F6',
    title: 'OBD2 Fault Code Decoding System™',
    subtitle: 'Understand What Diagnostic Trouble Codes Actually Mean',
    intro:
      'Before investigating a fault, you need to understand the information your scanner provides. I’ll walk you through the structure of OBD2 diagnostic trouble codes and how to interpret their meaning.',
    items: [
      'Understanding OBD2 diagnostic trouble codes',
      'Generic versus manufacturer-specific codes',
      'Powertrain fault code categories',
      'The meaning of P0, P1, P2, and P3 code families',
      'How to interpret code descriptions correctly',
      'Confirmed, pending, and permanent DTC concepts',
      'Understanding freeze-frame information',
      'How to organize multiple diagnostic codes',
      'Common mistakes when interpreting scanner results',
      'The Fault Code Investigation Worksheet',
    ],
    result: 'A clearer understanding of what your scanner is reporting and which information matters before testing.',
  },
  {
    numeral: 'II',
    color: '#22C55E',
    title: 'Check Engine Diagnostic Roadmap™',
    subtitle: 'Follow a Structured Path From Warning Light to Diagnostic Investigation',
    intro:
      'A Check Engine light can appear for many different reasons. Instead of treating every code as an instruction to replace a component, this volume shows you how to organize a logical investigation.',
    items: [
      'The Check Engine investigation framework',
      'Initial vehicle information collection',
      'Symptom classification procedures',
      'Diagnostic code prioritization',
      'How to organize related fault codes',
      'Decision trees for selecting investigative steps',
      'A framework for reviewing freeze-frame conditions',
      'When to consult manufacturer-specific procedures',
      'The Check Engine Diagnostic Planning Matrix',
      'A structured investigation checklist',
    ],
    result: 'A repeatable process for organizing Check Engine investigations without jumping directly to conclusions.',
  },
  {
    numeral: 'III',
    color: '#EAB308',
    title: 'Automotive Sensor Diagnostic Blueprint™',
    subtitle: 'Understand Sensor-Related Fault Codes and Organize the Right Tests',
    intro:
      'Sensors provide critical information to electronic control modules. But a sensor-related fault code doesn’t always mean the sensor itself is defective.',
    items: [
      'MAP sensor diagnostic fundamentals',
      'MAF sensor investigation principles',
      'Throttle position sensor concepts',
      'Crankshaft and camshaft position sensor fundamentals',
      'Oxygen sensor diagnostic considerations',
      'Engine coolant temperature sensor interpretation',
      'Intake air temperature sensor fundamentals',
      'Sensor power supply and ground verification concepts',
      'Signal plausibility and correlation checks',
      'Sensor diagnostic worksheets',
    ],
    result: 'A clearer framework for investigating sensor-related problems using evidence rather than assumptions.',
  },
  {
    numeral: 'IV',
    color: '#EF4444',
    title: 'OBD2 Live Data Interpretation System™',
    subtitle: 'Turn Scanner Readings Into Useful Diagnostic Evidence',
    intro:
      'Your scanner can provide much more information than fault codes. Live data helps you understand operating conditions, sensor behavior, and relationships between measurements.',
    items: [
      'Understanding OBD2 live data parameters',
      'Short-term and long-term fuel trim concepts',
      'Engine temperature data interpretation',
      'MAP and MAF reading fundamentals',
      'Oxygen sensor and air-fuel feedback concepts',
      'Engine RPM and load relationships',
      'Freeze-frame versus live data',
      'Identifying potentially inconsistent readings',
      'Comparing observations against reliable specifications',
      'Live Data Analysis Worksheets',
    ],
    result: 'A structured way to record, compare, and interpret diagnostic data before selecting additional tests.',
  },
  {
    numeral: 'V',
    color: '#A855F7',
    title: 'Automotive Fault Investigation Master System™',
    subtitle: 'Connect Symptoms, Codes, Measurements, and Possible Causes',
    intro:
      'You have the symptoms. You have the codes. You have the available data. Now determine what the evidence supports and what still needs to be checked.',
    items: [
      'The Diagnostic Evidence Matrix',
      'Fault code versus root cause analysis',
      'Symptom-to-system investigation maps',
      'Multiple-code investigation frameworks',
      'Electrical versus mechanical fault considerations',
      'Hypothesis tracking worksheets',
      'Test selection decision trees',
      'Expected versus observed result comparisons',
      'Diagnostic dead-end review procedures',
      'The Next Diagnostic Step Planner',
    ],
    result: 'A more organized process for evaluating possible causes and deciding how to continue an investigation.',
  },
  {
    numeral: 'VI',
    color: '#14B8A6',
    title: 'Diagnostic Verification & Case Documentation Roadmap™',
    subtitle: 'Document Your Findings and Verify the Outcome',
    intro:
      'A diagnostic investigation shouldn’t end simply because a code disappears. Understand what was found, what action was taken, and whether the original problem has been verified.',
    items: [
      'Professional diagnostic case documentation',
      'Initial symptom and fault code records',
      'Pre-repair findings summaries',
      'Test result documentation templates',
      'Repair and intervention records',
      'Post-repair verification planning',
      'Diagnostic trouble code recheck procedures',
      'Remaining fault documentation',
      'Case closure worksheets',
      'Building your own diagnostic reference library',
    ],
    result: 'A clearer record of each diagnostic investigation and a repeatable framework for reviewing future cases.',
  },
];

export const toolkit = [
  {
    icon: 'search',
    title: 'OBD2 Fault Code Reference Guides',
    body: 'Understand code categories, common descriptions, and the information needed to begin investigating.',
  },
  {
    icon: 'map',
    title: 'Visual Diagnostic Roadmaps',
    body: 'Follow illustrated workflows that organize the investigation from the initial symptom to the next logical test.',
  },
  {
    icon: 'chart',
    title: 'Live Data Analysis Worksheets',
    body: 'Record scanner readings, operating conditions, expected values, and observations in one place.',
  },
  {
    icon: 'zap',
    title: 'Sensor Investigation Checklists',
    body: 'Organize the information required when investigating sensor-related codes and electronic signals.',
  },
  {
    icon: 'clipboard',
    title: 'Professional Diagnostic Case Templates',
    body: 'Document fault codes, measurements, hypotheses, repairs, and verification results.',
  },
] as const;

export const bonuses = [
  {
    number: 1,
    title: '100 OBD2 Fault Codes Quick Reference Library™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/0a0be385-6af9-406a-9faf-4e43a2c1d525.jpg',
    description: 'A practical reference collection covering 100 common generic OBD2 diagnostic trouble codes.',
    items: [
      '100 common OBD2 code descriptions',
      'Code category identification',
      'Systems associated with each code',
      'Common diagnostic considerations',
      'Possible contributing conditions',
      'Suggested information to review',
      'Diagnostic investigation notes',
    ],
    why: 'Quickly understand a code’s general meaning and identify what additional information may be needed.',
  },
  {
    number: 2,
    title: 'The 10-Minute OBD2 Diagnostic Starter Protocol™',
    value: 27,
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/0c64b1c9-6f43-4f43-b8d6-eaf300d24ff2.jpg',
    description: 'A focused workflow for organizing the beginning of an OBD2 diagnostic investigation.',
    items: [
      'Initial symptom recording',
      'Fault code collection checklist',
      'Freeze-frame information review',
      'Vehicle operating condition notes',
      'Initial evidence classification',
      'Missing information checklist',
      'Next-step investigation planner',
    ],
    why: 'Organize your initial findings and select a logical next investigative step in approximately 10 minutes.',
  },
  {
    number: 3,
    title: '50 Automotive Diagnostic Case Studies™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/95288734-addd-45b0-9728-9ad23bd1ea35.jpg',
    description:
      'Illustrated educational scenarios following one structure: Symptom → Fault Code → Available Evidence → Diagnostic Questions → Suggested Investigation → Verification.',
    items: [
      'Check Engine diagnostic scenarios',
      'Sensor-related investigation examples',
      'Fuel trim interpretation cases',
      'Misfire investigation scenarios',
      'Emissions-related diagnostic examples',
      'Multiple-code investigation exercises',
      'Evidence-based decision examples',
    ],
    why: 'Learn how to approach different diagnostic situations through practical examples.',
  },
  {
    number: 4,
    title: 'OBD2 Live Data Quick Interpretation Atlas™',
    value: 27,
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/245d0fe4-d44b-4517-aba9-599f6d164fc3.jpg',
    description: 'A visual reference guide to commonly used OBD2 live data parameters.',
    items: [
      'Fuel trim interpretation diagrams',
      'Engine temperature data concepts',
      'MAP and MAF parameter explanations',
      'Oxygen sensor feedback illustrations',
      'Engine load and RPM relationships',
      'Common data interpretation mistakes',
      'Live data comparison worksheets',
    ],
    why: 'Build a clearer understanding of what different scanner readings represent and how to evaluate them.',
  },
  {
    number: 5,
    title: '30 Automotive Diagnostic Checklists™',
    value: 29,
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1bc0b2bc-bf57-451a-9782-0083a6646f8c.jpg',
    description: '30 printable checklists covering every stage of an automotive diagnostic investigation.',
    items: [
      'Initial vehicle assessment',
      'OBD2 scanner information collection',
      'Fault code interpretation',
      'Sensor investigation planning',
      'Live data documentation',
      'Electrical testing preparation',
      'Hypothesis evaluation',
      'Repair documentation',
      'Post-repair verification',
      'Diagnostic case closure',
    ],
    why: 'Keep your investigation organized and reduce the chance of overlooking important documentation steps.',
  },
];

export const offerIncludes = [
  'Six professional digital volumes',
  'Five exclusive diagnostic bonuses',
  'Visual diagnostic workflows',
  'Printable checklists and worksheets',
  'Practical troubleshooting frameworks',
  'Instant digital access',
  'Lifetime access',
  'No monthly subscriptions',
];

export const testimonials = [
  {
    name: 'Carlos Mendoza',
    role: 'Automotive Technician',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/4bf48298-652e-444b-b7e8-e0a1f6d36d1c.jpg',
    quote:
      'What I find most useful about this approach is the separation between the fault code and the actual cause. Having a structured process makes it easier to decide what information I need before testing components.',
  },
  {
    name: 'Michael Harris',
    role: 'Automotive Diagnostics Specialist',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/901207e7-7d9d-4a68-b709-0dbb7befd2b8.jpg',
    quote:
      'The live data worksheets are exactly the kind of resource I want beside my scanner. They help organize the readings and make it easier to review what has already been checked.',
  },
  {
    name: 'Javier Ramírez',
    role: 'Independent Workshop Technician',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a7a7e06d-b7a9-4e8e-a42f-9c5eebe90a77.jpg',
    quote:
      'I like having a consistent process for Check Engine investigations. Instead of jumping between possible causes, I document the evidence and work through the case logically.',
  },
  {
    name: 'Emily Watson',
    role: 'Automotive Electronics Student',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/5c766aee-6d11-47e5-8401-62b4ba31e90d.jpg',
    quote:
      'The diagnostic case studies make the concepts easier to understand. Seeing how symptoms, codes, and test results connect is especially useful when learning electronic diagnosis.',
  },
  {
    name: 'Robert Jenkins',
    role: 'Automotive Workshop Owner',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/7b1b390d-8e4e-47bf-8927-77d0edae9311.jpg',
    quote:
      'A good diagnostic system doesn’t replace experience. It helps organize it. That’s what makes a structured reference collection valuable in a busy workshop.',
  },
  {
    name: 'Luis Fernández',
    role: 'Mobile Diagnostic Technician',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/f1837b50-0262-4c2e-abd3-58c2e78dd40d.jpg',
    quote:
      'Working out of a van, I can’t afford to guess. The 10-Minute Starter Protocol gives me a clear first plan on every job, and my customers notice I explain the next step with confidence.',
  },
];

export const authorQuestions = [
  'What does the code actually indicate?',
  'What conditions were present when it was recorded?',
  'Which systems could be involved?',
  'What information is missing?',
  'Which test would help confirm or eliminate a possible cause?',
  'And how will you verify the outcome?',
];

export const faqs = [
  {
    q: 'How will I receive the OBD2 Diagnostic Blueprint™?',
    a: 'The product is delivered digitally. After your payment is confirmed, you’ll receive instructions for accessing your materials. No physical product will be shipped.',
  },
  {
    q: 'Is this suitable for beginners?',
    a: 'Yes. The collection explains important OBD2 diagnostic concepts in an organized way. Beginners can develop their understanding of fault codes, scanner information, and diagnostic reasoning, while experienced technicians may find the workflows, reference guides, and documentation tools useful. Basic automotive knowledge is recommended.',
  },
  {
    q: 'Does this work with every vehicle?',
    a: 'The Blueprint explains general OBD2 diagnostic concepts relevant to many OBD2-equipped vehicles. Vehicle systems, code definitions, specifications, and testing procedures vary by manufacturer, model, and year — always consult the applicable manufacturer service information.',
  },
  {
    q: 'Will this tell me exactly which component is faulty?',
    a: 'No educational reference can reliably identify every faulty component from a diagnostic trouble code alone. The Blueprint helps you understand codes, organize evidence, and plan appropriate investigations. Actual fault identification requires suitable testing and verification.',
  },
  {
    q: 'Do I need an expensive diagnostic scanner?',
    a: 'You can study the educational content without owning an advanced scanner. For practical vehicle diagnosis, you’ll need equipment appropriate to the vehicle and the tests being performed.',
  },
  {
    q: 'Does the Blueprint explain live OBD2 data?',
    a: 'Yes. Volume IV focuses on understanding common OBD2 live data parameters, organizing observations, and interpreting measurements in the context of vehicle operating conditions.',
  },
  {
    q: 'Does it include real diagnostic examples?',
    a: 'The collection includes 50 illustrated educational case studies showing how different diagnostic situations can be investigated, step by step.',
  },
  {
    q: 'Can I download the files?',
    a: 'Yes. The offer provides downloadable digital materials you can keep on your computer, tablet, or phone.',
  },
  {
    q: 'Is this a monthly subscription?',
    a: 'No. The OBD2 Diagnostic Blueprint™ is a one-time payment of $27 USD. There are no recurring charges.',
  },
  {
    q: 'Will I receive future updates?',
    a: 'Your purchase includes the digital materials available at the time of purchase. Future updates depend on the seller’s published update policy.',
  },
  {
    q: 'Can I use this outside the United States?',
    a: 'Yes. The concepts are useful wherever compatible OBD2 diagnostic systems are used. The price is automatically converted to your local currency at checkout.',
  },
  {
    q: 'Does this replace manufacturer service manuals?',
    a: 'No. The Blueprint is an educational and organizational resource. It does not replace manufacturer-specific procedures, technical specifications, safety instructions, or professional judgment.',
  },
  {
    q: 'What if the product isn’t right for me?',
    a: 'You’re covered by our 7-day money-back guarantee. Request a refund within seven days of purchase according to the published terms.',
  },
];

export const finalRecap = [
  'A clearer understanding of the fault code',
  'A structured process for collecting diagnostic information',
  'Visual roadmaps for organizing possible causes',
  'Better ways to interpret and document live data',
  'Checklists to guide your investigation',
  'A repeatable framework for planning the next diagnostic step',
];

export const recentBuyers = [
  { name: 'María González', country: 'Mexico' },
  { name: 'Santiago Herrera', country: 'Colombia' },
  { name: 'Lucía Fernández', country: 'Argentina' },
  { name: 'Diego Morales', country: 'Chile' },
  { name: 'Andrés Castillo', country: 'Peru' },
  { name: 'Valentina Rojas', country: 'Ecuador' },
  { name: 'José Martínez', country: 'Spain' },
  { name: 'Camila Pérez', country: 'Uruguay' },
  { name: 'Miguel Torres', country: 'Guatemala' },
  { name: 'Daniela Vargas', country: 'Costa Rica' },
  { name: 'Ricardo Sánchez', country: 'Dominican Republic' },
  { name: 'Fernando López', country: 'Bolivia' },
  { name: 'Gabriela Núñez', country: 'Paraguay' },
  { name: 'Alejandro Ruiz', country: 'Venezuela' },
  { name: 'Sofía Ramírez', country: 'Panama' },
];
